import { useEffect } from 'react';
import { authenticateInIframe, isInIframe } from '../services/iframeAuth';
import {
  getCalendarAuthorizedFromJWT,
  getJWTSecondsUntilExpiry,
  isAuthenticatedJWT,
} from '../utils/auth';

const NO_REDIRECT_PATHS = [
  '/authorize-calendar',
  '/calendar-auth-success',
  '/authorization-error',
  '/error',
  '/user-not-found',
  '/public/',
];

/**
 * Marge (s) avant expiration en-dessous de laquelle on (re)demande un token au
 * parent. Les access tokens Keycloak sont courts (~5 min) : on rafraîchit ~90s
 * avant expiration. Au-dessus de cette marge, on ne relance pas de postMessage
 * au (re)montage (évite le spam quand le parent change la `src` de l'iframe).
 */
const REFRESH_MARGIN_SEC = 90;

/** Période de vérification du refresh proactif (ms). */
const REFRESH_CHECK_INTERVAL_MS = 30 * 1000;

function shouldSkipCalendarRedirect(): boolean {
  return NO_REDIRECT_PATHS.some((p) => window.location.pathname.startsWith(p));
}

function redirectToAuthorizeCalendarIfNeeded() {
  if (shouldSkipCalendarRedirect()) return;
  window.location.href = '/authorize-calendar';
}

/**
 * Hook global déclenché au démarrage de l'app (modèle Keycloak-direct).
 *
 * Comportement en iframe :
 * - Auth initiale : on ne (re)demande un token Keycloak au parent que si le token
 *   courant est absent ou proche de l'expiration (`ttl <= REFRESH_MARGIN_SEC`).
 *   `authenticateInIframe()` récupère le token + l'état `calendar_authorized` via
 *   GET /api/me (cet état n'est plus porté par le token). Si le token est encore
 *   valide, on laisse l'app démarrer : une éventuelle ré-autorisation calendrier
 *   sera déclenchée à la volée par l'API (401 `calendar_reauthorization_required`
 *   géré dans l'intercepteur axios).
 * - Refresh proactif : un timer rafraîchit le token Keycloak (court, ~5 min) avant
 *   son expiration pour éviter des 401 en plein usage. L'intercepteur axios reste
 *   un filet de sécurité sur 401.
 * - En cas d'échec (parent ne répond pas), on conserve le token courant ; le
 *   prochain 401 relancera l'auth.
 */
export function useIframeAuthBoot() {
  useEffect(() => {
    if (!isInIframe()) {
      return;
    }

    let cancelled = false;

    const doAuth = () =>
      authenticateInIframe()
        .then((data) => {
          if (!cancelled && !data.calendar_authorized) {
            redirectToAuthorizeCalendarIfNeeded();
          }
        })
        .catch(() => {
          if (!cancelled && isAuthenticatedJWT() && getCalendarAuthorizedFromJWT() === false) {
            redirectToAuthorizeCalendarIfNeeded();
          }
        });

    // Auth initiale conditionnelle (évite un postMessage à chaque remontage).
    const ttl = getJWTSecondsUntilExpiry();
    if (ttl === null || ttl <= REFRESH_MARGIN_SEC) {
      doAuth();
    }

    // Refresh proactif avant expiration du token Keycloak.
    const intervalId = window.setInterval(() => {
      const t = getJWTSecondsUntilExpiry();
      if (t === null || t < REFRESH_MARGIN_SEC) {
        doAuth();
      }
    }, REFRESH_CHECK_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);
}

export default useIframeAuthBoot;
